'use-client';
import React, { useEffect, useState } from 'react';
import { LineItem } from 'shared/types/cart';
import { PersonalizeOptions } from 'shared/types/cart/Cart';
import { Variant } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import ProtectionPlanIcon from '@/components/customercenter-ui/icons/pdpProtection';
import { useCart, useProduct } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision, getSelectedSite } from '@/helpers/utils/siteUtils';
import LearnMore from '../product/details/components/pdpProtectionPlan/learnMore';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

interface YearPlanProps {
  year: string;
  price: string;
  ppm: string;
  selected: boolean;
  onSelect: () => void;
}

interface Props {
  lineItem?: LineItem;
  selectedWarranty?: LineItem;
  setIsDataLoading: (value: boolean) => void;
  isSaveForLater?: boolean;
  isFromDrawer?: boolean;
}

const YearPlan: React.FC<YearPlanProps> = ({ year, price, ppm, selected, onSelect }) => {
  const selectedSite = getSelectedSite();
  const [ppmClass, setPpmClass] = useState('ppmZBColorClass');

  useEffect(() => {
    if (selectedSite === 'ZBSite') {
      setPpmClass('ppmZBColorClass');
    } else if (selectedSite === 'ZYSite') {
      setPpmClass('ppmZYColorClass');
    } else if (selectedSite === 'ZDSite') {
      setPpmClass('ppmZDColorClass');
    }
  }, [selectedSite]);

  return (
    <li
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={`flex cursor-pointer items-center gap-4 rounded-md bg-white shadow-sm ${
        selected ? 'border-2 border-forestGreen px-[15px] py-[11px]' : 'border border-foggyGray px-4 py-3'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="size-6 pointer-events-none shrink-0 rounded-sm border-2 border-black bg-white text-forestGreen shadow-none checked:!border-forestGreen checked:!bg-forestGreen focus:outline-none focus:ring-0"
      />
      <span className="flex flex-col text-left">
        <span className="text-sm leading-5 text-black md:text-base">
          <strong>
            {year} {mapLocaleToMeaningfulFormat().protectionPlan}
          </strong>
          {price && <> - {CurrencyHelpers.formatForCurrency(price)}</>}
        </span>
        {ppm && <span className={`text-xs font-semibold leading-[18px] md:text-sm ${ppmClass}`}>{ppm}</span>}
      </span>
    </li>
  );
};

const AddProtectionPlan: React.FC<Props> = ({ lineItem, selectedWarranty, isSaveForLater }) => {
  const { replaceCartLineItem, removeItem } = useCart(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const { getWarrantyProducts } = useProduct();
  const projectSettings: ProjectSettings = useProjSettings();
  const [warrantyProductData, setWarrantyProductData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const siteDivision = getSelectedDivision();
  const siteId = getSelectedSite() ?? 'ZBSite';
  const listPrice = isSaveForLater ? selectedWarranty?.variant?.price?.listPrice : selectedWarranty?.listPrice;
  const price: any = isSaveForLater ? selectedWarranty?.variant?.price?.value : selectedWarranty?.price;
  const isWarrantyEnabledSite = projectSettings?.isWarrantyEnabledSite?.[siteDivision ?? ''] ?? false;
  const parentDepartment = lineItem?.variant?.attributes?.department ?? '';
  const parentProductPrice = lineItem?.listPrice ?? lineItem?.price;
  const formattedListPrice = CurrencyHelpers.formatForCurrency(listPrice || 0).replace(/[^0-9.,-]/g, '');
  const warrantyMinPricePerMonth = projectSettings?.warrantyMinPricePerMonth;
  const pricePerMonth =
    listPrice && Number(formattedListPrice) > warrantyMinPricePerMonth
      ? isSaveForLater
        ? selectedWarranty?.variant?.price?.pricePerMonth
        : selectedWarranty?.pricePerMonth
      : undefined;

  useEffect(() => {
    if (selectedWarranty) {
      setSelectedPlan(selectedWarranty?.variant?.attributes?.variantSelectorValue1);
    }
  }, []);

  const fetchWarrantyProduct = async () => {
    if (parentDepartment && parentProductPrice?.centAmount) {
      const warrantyProducts = await getWarrantyProducts({
        parentProductPrice: parentProductPrice,
        departmentName: parentDepartment,
      });
      setWarrantyProductData(warrantyProducts);
    }
  };

  useEffect(() => {
    if (isWarrantyEnabledSite && !isSaveForLater) {
      if (parentDepartment && parentProductPrice?.centAmount) {
        fetchWarrantyProduct();
      }
    }
  }, []);

  const handlePlanSelect = async (year: string, sku: string) => {
    let removeWarrantyItem = false;
    if (year === selectedPlan) {
      removeWarrantyItem = true;
      setSelectedPlan(null);
    } else {
      setSelectedPlan(year);
    }
    try {
      setLoading(true);
      let personalizeOptions: PersonalizeOptions = {};
      if (
        lineItem?.variant?.attributes?.personalizeOption &&
        lineItem?.personalizeText &&
        lineItem?.personalizeProductId
      ) {
        personalizeOptions = {
          personalizeText: lineItem?.personalizeText,
          personalizeProductId: lineItem?.personalizeProductId,
          personalizeUserId: lineItem?.personalizeUserId,
        };
      }
      const warrantyOptions =
        sku && !removeWarrantyItem
          ? {
              isWarrantySelected: true,
              sku,
              id: selectedWarranty?.lineItemId,
            }
          : undefined;

      await replaceCartLineItem(
        lineItem?.lineItemId as string,
        lineItem?.count as number,
        lineItem?.variant as Variant,
        warrantyOptions,
        personalizeOptions,
      );
      if (removeWarrantyItem && selectedWarranty?.lineItemId) {
        await removeItem(selectedWarranty?.lineItemId);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error while handling plan selection:', error);
    }
  };

  return (
    <section>
      {loading && (
        <section className="top-[calc(50%_-_15px] z-2 size-[30px] absolute left-[calc(50%_-_15px)]">
          <Loader />
        </section>
      )}
      {warrantyProductData && !isSaveForLater ? (
        <Accordion type="single" collapsible>
          <AccordionItem value="item-1" className="rounded-md border border-foggyGray bg-lightSilver">
            <AccordionTrigger className="font-sm gap-4  rounded-t-md p-3 font-medium leading-[19.8px] text-black">
              <>
                {selectedWarranty ? (
                  <div className="added relative flex w-full gap-2">
                    <ProtectionPlanIcon className="size-6 shrink-0 " />
                    <section className="mt-0.5">
                      <section className="flex text-sm font-normal leading-[19.4px] text-black">
                        <strong className="pr-1">{selectedWarranty?.variant?.attributes?.abwDescription}:</strong>
                        {listPrice && listPrice?.centAmount !== price?.centAmount ? (
                          <p className="break-words text-13 font-normal leading-[18.2px] text-black">
                            <strong className={`${clearanceTextClass(siteId)}`}>
                              {CurrencyHelpers.formatForCurrency(price || 0)}
                            </strong>{' '}
                            <span className="line-through">{CurrencyHelpers.formatForCurrency(listPrice)}</span>
                          </p>
                        ) : (
                          <p className={`leading-[18.2px]text-black break-words text-13 font-normal`}>
                            {CurrencyHelpers.formatForCurrency(price || 0)}
                          </p>
                        )}
                      </section>
                      {pricePerMonth && (
                        <p className="flex text-13 font-normal leading-[18.2px] text-black">
                          {
                            mapLocaleToMeaningfulFormat([CurrencyHelpers.formatForCurrency(pricePerMonth)?.toString()])
                              .morePricePerMonth
                          }{' '}
                        </p>
                      )}
                    </section>
                  </div>
                ) : (
                  <span className="flex w-full items-center">
                    <ProtectionPlanIcon className="size-6 mr-1 shrink-0" />
                    {mapLocaleToMeaningfulFormat().addProtectionPlan}
                  </span>
                )}
              </>
            </AccordionTrigger>
            <AccordionContent className="border-t border-foggyGray p-3 md:p-4">
              <section className="flex items-center gap-3">
                <section className="flex w-[90px] shrink-0 flex-col items-center gap-2 text-center text-black">
                  <h2 className="text-base font-bold leading-6">{mapLocaleToMeaningfulFormat().protectYourPurchase}</h2>
                  <section className="text-sm">
                    <LearnMore
                      department={lineItem?.variant?.attributes?.department}
                      classValue={lineItem?.variant?.attributes?.class}
                      subClassValue={lineItem?.variant?.attributes?.subClass}
                    />
                  </section>
                </section>
                <ul className="flex min-w-0 flex-1 flex-col gap-3">
                  {warrantyProductData?.map((warrantyProduct: any) => (
                    <YearPlan
                      key={warrantyProduct?.warrantyVariant ?? ''}
                      year={warrantyProduct?.displayName ?? ''}
                      price={warrantyProduct?.listPrice ?? ''}
                      ppm={
                        warrantyProduct?.pricePerMonth && warrantyProduct?.pricePerMonth?.centAmount
                          ? warrantyProduct?.pricePerMonth?.centAmount > 0
                            ? `Only $${warrantyProduct?.pricePerMonth?.centAmount / 100} more per month*`
                            : ''
                          : ''
                      }
                      selected={selectedPlan === warrantyProduct?.displayName}
                      onSelect={() =>
                        handlePlanSelect(warrantyProduct?.displayName ?? '', warrantyProduct?.warrantyVariant ?? '')
                      }
                    />
                  ))}
                </ul>
              </section>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        selectedWarranty &&
        isSaveForLater && (
          <div className="added relative mt-4 flex w-full gap-2 rounded-md border border-foggyGray bg-lightSilver p-3 focus:outline-none">
            <ProtectionPlanIcon className="size-6 shrink-0 " />
            <section className="mt-0.5">
              <section className="flex text-sm font-normal leading-[19.4px] text-black">
                <strong className="pr-1">{selectedWarranty?.variant?.attributes?.abwDescription}:</strong>
                {listPrice && listPrice?.centAmount !== price?.centAmount ? (
                  <p className="break-words text-13 font-normal leading-[18.2px] text-black">
                    <strong className={`${clearanceTextClass(siteId)}`}>
                      {CurrencyHelpers.formatForCurrency(price || 0)}
                    </strong>{' '}
                    <span className="line-through">{CurrencyHelpers.formatForCurrency(listPrice)}</span>
                  </p>
                ) : (
                  <p className={`leading-[18.2px]text-black break-words text-13 font-normal`}>
                    {CurrencyHelpers.formatForCurrency(price || 0)}
                  </p>
                )}
              </section>
              {pricePerMonth && (
                <p className="text-13 font-normal leading-[18.2px] text-black">
                  {
                    mapLocaleToMeaningfulFormat([CurrencyHelpers.formatForCurrency(pricePerMonth)?.toString()])
                      .morePricePerMonth
                  }{' '}
                </p>
              )}
            </section>
          </div>
        )
      )}
    </section>
  );
};

export default AddProtectionPlan;
